/**
 * Course Store
 *
 * Zustand store for dynamic language course configuration.
 * Fetches from GET /api/courses, caches in AsyncStorage, falls back to empty state.
 *
 * Data lifecycle:
 * 1. Initialize with empty state
 * 2. Load from AsyncStorage cache (if available)
 * 3. Fetch from API in background
 * 4. On success: update store + cache (full replace, no merge)
 * 5. On failure: continue with bundled seed data
 *
 * courseStore.load() is awaited in AppNavigator before the splash screen hides,
 * so the store is always populated before any consumer renders.
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { loadCourseCache } from '../services/courseCache';
import { CoursesCacheStorage } from '../generated/sync/sync-config';
import { freshnessManager } from '../services/freshnessManager';
import { createLogger } from '../utils/logger';
import type { LanguageCourse, LocaleMetadataFull } from '../types/locale';
import { setDefaultVariants, FLAGSHIP_COURSE_ID } from '../types/locale';
import type { CountryLocaleConfig } from '../data/countryConfig';
import { seedCourses, seedLocales, seedCountries } from '../generated/seeds/course-seed';

const log = createLogger('CourseStore');

// Initialize default variants from seed data at module load time
setDefaultVariants(seedCourses);

// ── Types ────────────────────────────────────────────────────────

/**
 * Surfaceable failure record for a fresh-install `load()` that couldn't
 * produce a usable course set (no cache + API failed, or API returned
 * empty courses). Drives `CourseLoadErrorScreen` per DCOURSE-007.
 *
 * `kind` mirrors the generated API client union — `domain` for HTTP
 * responses (the backend reached us but returned a non-2xx with a code)
 * vs `transport` for network/abort failures. `code` is a best-effort
 * diagnostic — e.g. `503`, `NETWORK_ERROR`, `EMPTY_COURSES` — surfaced
 * in the retry screen so operators triaging a failed boot have a hook.
 *
 * Cleared on every successful `load()` and on retry-attempt start so
 * the UI flips back to a neutral pending state during the retry.
 */
export interface CourseLoadError {
  kind: 'domain' | 'transport' | 'empty';
  code?: string;
}

interface CourseState {
  courses: LanguageCourse[];
  locales: LocaleMetadataFull[];
  countries: CountryLocaleConfig[];
  loaded: boolean;
  /**
   * Last fatal load failure. Non-null only when `loaded === false` —
   * a subsequent cache hit or successful API fetch flips `loaded` to
   * true and `loadError` back to null in the same set() call. The
   * AppNavigator gates on `loadError !== null && !loaded` to render
   * `CourseLoadErrorScreen` (DCOURSE-007). Once `loaded === true`,
   * a later API failure is non-fatal (DCOURSE-002-03) and does NOT
   * populate this field — the user keeps using cached data silently.
   */
  loadError: CourseLoadError | null;
  /**
   * True while a `load()` call is in flight. Drives the retry-button
   * pending state in `CourseLoadErrorScreen` so double-taps don't fire
   * concurrent fetches.
   */
  loading: boolean;

  /** Load from cache then fetch from API. Call once at app startup. */
  load: () => Promise<void>;
  /** Load from cache only — called after generated refresh saves raw data. */
  loadFromCache: () => Promise<void>;
}

// ── Store ────────────────────────────────────────────────────────

export const useCourseStore = create<CourseState>((set, get) => ({
  // Initialize with bundled seed data — updated by load() from cache/API
  courses: seedCourses,
  locales: seedLocales,
  countries: seedCountries,
  loaded: false,
  loadError: null,
  loading: false,

  load: async () => {
    // Mark in-flight + clear any prior failure so the retry screen flips
    // to its "trying again…" state immediately on tap. Concurrent calls
    // (a retry click while a background refresh is also running) just
    // re-enter — Zustand state stays consistent because the in-flight
    // request observed at any later `set()` is the LAST one to land,
    // and either path lands on the same shape.
    set({ loadError: null, loading: true });

    // Step 1: Try to load from cache
    try {
      const cached = await loadCourseCache();
      if (cached) {
        // loadCourseCache returns fully shaped data (transform on read)
        setDefaultVariants(cached.courses);
        set({
          courses: cached.courses,
          locales: cached.locales,
          countries: cached.countries,
          loaded: true,
                });

        // If freshnessManager says courses is fresh (within TTL since
        // last successful sync), skip the API fetch. Single source of
        // truth for the staleness check — no per-cache `fetchedAt` field
        // needed.
        if (freshnessManager.isFresh('courses')) {
          set({ loading: false });
          return;
        }
      }
    } catch (error) {
      log.warn('Failed to load course cache', { error });
    }

    // Step 2: Fetch from API (background refresh or first load)
    const result = await api.getCourses();

    if (!result.ok) {
      // If cache was already loaded (step 1), API failure is non-fatal — we have real data
      if (get().loaded) {
        log.warn('Failed to refresh courses from API, using cached data', { kind: result.kind, error: result.error });
        set({ loading: false });
        return;
      }
      // No cache and API failed — surface a retry screen instead of
      // throwing. The AppNavigator gate (DCOURSE-007) renders
      // `CourseLoadErrorScreen` while loadError is non-null and
      // loaded is still false.
      const code =
        result.error &&
        typeof result.error === 'object' &&
        'code' in result.error &&
        typeof (result.error as { code?: unknown }).code === 'string'
          ? (result.error as { code: string }).code
          : undefined;
      log.error('Failed to load courses from cache or API — surfacing retry screen', {
        kind: result.kind,
        error: result.error,
      });
      set({
        loadError: { kind: result.kind, ...(code !== undefined ? { code } : {}) },
        loading: false,
      });
      return;
    }

    const data = result.data;

    if (!data.courses || data.courses.length === 0) {
      // Empty courses on first install = same hard-gate as a network
      // failure. The seed array stays bundled for module-load constants
      // (DCOURSE-003) but never becomes the user-visible course list.
      if (!get().loaded) {
        log.error('API returned empty courses on first load — surfacing retry screen');
        set({
          loadError: { kind: 'empty', code: 'EMPTY_COURSES' },
          loading: false,
        });
        return;
      }
      // Cache already present — empty refresh response is a backend
      // hiccup, not a fatal state. Keep what we have.
      log.warn('API returned empty courses on refresh, keeping cached data');
      set({ loading: false });
      return;
    }

    const courses = data.courses as LanguageCourse[];
    const locales = data.locales as LocaleMetadataFull[];
    setDefaultVariants(courses);
    set({
      courses,
      locales,
      countries: data.countries as CountryLocaleConfig[],
      loaded: true,
      loadError: null,
      loading: false,
    });

    // Persist to cache — data is already in final form. Cache schema
    // mirrors the API response exactly (no extra fields), so a direct
    // save is correct. Freshness is recorded separately via
    // freshnessManager.markFresh below.
    try {
      await CoursesCacheStorage.save(data as unknown as Parameters<typeof CoursesCacheStorage.save>[0]);
    } catch (err) {
      log.warn('Failed to save course cache', { error: err });
    }

    // Mark courses domain as fresh after successful API fetch
    try {
      freshnessManager.markFresh('courses');
    } catch { /* freshnessManager not initialized yet on first call */ }
  },

  loadFromCache: async () => {
    try {
      const cached = await loadCourseCache();
      if (cached) {
        setDefaultVariants(cached.courses);
        set({
          courses: cached.courses,
          locales: cached.locales,
          countries: cached.countries,
          loaded: true,
                });
      }
    } catch (error) {
      log.warn('Failed to load course cache', { error });
    }
  },
}));

// ── Non-React accessors ──────────────────────────────────────────

/** Get current courses (from store state). */
export function getCourses(): LanguageCourse[] {
  return useCourseStore.getState().courses;
}

/** Get current locale metadata (from store state). */
export function getLocalesData(): LocaleMetadataFull[] {
  return useCourseStore.getState().locales;
}

/** Look up locale metadata by code. */
export function getLocaleByCode(code: string): LocaleMetadataFull | undefined {
  return useCourseStore.getState().locales.find((l) => l.code === code);
}

/** Get current country configs (from store state). */
export function getCountriesData(): CountryLocaleConfig[] {
  return useCourseStore.getState().countries;
}

/**
 * Find the course that matches a source locale (by base language).
 * Returns the course and its default target locale, or undefined if no match.
 */
export function findCourseForLocale(sourceLocale: string): {
  course: LanguageCourse;
  defaultTargetLocale: string;
} | undefined {
  const base = sourceLocale.split('-')[0];
  const course = useCourseStore.getState().courses.find((c) => c.sourceBase === base);
  if (!course) return undefined;
  return { course, defaultTargetLocale: course.defaultTargetLocale };
}

// ── Validation fallback locales ──────────────────────────────────
// Used ONLY to coerce corrupt/invalid/unknown locale values into a
// concrete, usable course. Never a user-facing suggestion: decision
// points that can represent "unknown" explicitly (onboarding
// preselection, country locale options) must do so instead of
// reaching for these.
//
// The fallback course is chosen explicitly — flagship id, then any
// course with lessons — never by list position: the backend course
// list is ordered alphabetically by id, which would make the
// lesson-less de-es course the "default" by accident.

function fallbackCourse(): LanguageCourse {
  const courses = useCourseStore.getState().courses;
  if (courses.length === 0) {
    throw new Error('courseStore: no courses loaded — seed data missing');
  }
  return (
    courses.find((c) => c.id === FLAGSHIP_COURSE_ID) ??
    courses.find((c) => c.hasLessons) ??
    courses[0]
  );
}

/** Source locale used when a stored/derived value is corrupt or unknown. */
export function fallbackSourceLocale(): string {
  return fallbackCourse().defaultSourceLocale;
}

/** Target locale used when a stored/derived value is corrupt or unknown. */
export function fallbackTargetLocale(): string {
  return fallbackCourse().defaultTargetLocale;
}

// ── Metadata accessors (moved from locale.ts) ───────────────────

/** Get the Azure TTS voice for a locale. Returns '' if TTS not available. */
export function getDefaultTTSVoice(locale: string): string {
  const meta = getLocaleByCode(locale);
  return meta?.speech.azure?.ttsVoice ?? '';
}

/** Get the Azure STT locale for speech-to-text. Returns '' if STT not available. */
export function getAzureSttLocale(locale: string): string {
  const meta = getLocaleByCode(locale);
  return meta?.speech.azure?.sttLocale ?? '';
}
